import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormGroup,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import axios from "../../../utils/axiosInstance";
import { useSnackbar } from "notistack";
import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from "react";
import { MILLIS_TO_LOCALE } from "../../../utilities/helpers/formatUnix";
import { INGESTION_STATUS } from "../LandingPage/constants";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

export const PROXY_LOG_TYPE = "PROXY_LOG";

type ProxyLogsTableProps = {
    handleLoader: (value: boolean) => void;
};

const ProxyLogsTable = forwardRef(
    ({ handleLoader }: ProxyLogsTableProps, ref: any) => {
        const { enqueueSnackbar } = useSnackbar();
        const [rows, setRows] = useState<any[]>([]);
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);
        const [totalCount, setTotalCount] = useState(0);
        const [skipKeyQueue, setSkipKeyQueue] = useState<any[]>([]);
        const [filterOptions, setFilterOptions] = useState<any>({
            status: [...INGESTION_STATUS.map((item) => item.key)],
            completedAfter: null,
            completedBefore: null,
        });

        const onFilterOptionsChange = (filter: any) => {
            if (filter.hasOwnProperty("status")) {
                if (filterOptions.status.includes(filter.status)) {
                    filter.status = filterOptions.status.filter(
                        (status: string) => status != filter.status
                    );
                } else {
                    filter.status = [...filterOptions.status, filter.status];
                }
            }
            const updatedFilter = { ...filterOptions, ...filter };
            setFilterOptions(updatedFilter);
            getProxyLogsList({
                ...updatedFilter,
                status: updatedFilter.status,
            });
        };

        useImperativeHandle(
            ref,
            () => ({
                getProxyLogsList,
            }),
            [filterOptions, rowsPerPage]
        );

        useEffect(() => {
            getProxyLogsList();
        }, []);

        const resetTableMeta = () => {
            setSkipKeyQueue([]);
            setPage(0);
        };

        const getProxyLogsList = async (filters = filterOptions) => {
            resetTableMeta();
            const data = await fetchPage(rowsPerPage, "", [], filters);
            if (data) {
                setTotalCount(data.totalCount || 0);
                setRows([...(data.list || [])]);
            }
        };

        const fetchPage = async (
            limit: number | string,
            skip: string = "",
            skipQueue: any[] = skipKeyQueue,
            filters = filterOptions
        ) => {
            try {
                if (filters?.status.length == 0) {
                    filters.status = [
                        ...INGESTION_STATUS.map((item) => item.key),
                    ];
                }
                handleLoader(true);
                const url = `${
                    process.env.BASE_URL
                }api/v1/cms/ingest/proxy?limit=${limit}&skip=${skip}&status=${
                    filters?.status.join(",") || ""
                }&completedAfter=${filters?.completedAfter || ""}&completedBefore=${
                    filters?.completedBefore || ""
                }`;
                const response = await axios.get(url);
                const data = response.data?.data;
                const list = data?.list || [];
                if (list.length) {
                    const key = `${list[list.length - 1]?.id}__${
                        list[list.length - 1]?.lastModified
                    }`;
                    if (!skipQueue.includes(key)) {
                        setSkipKeyQueue([...skipQueue, key]);
                    }
                }
                return data;
            } catch (error: any) {
                enqueueSnackbar(
                    error?.response?.data?.message ||
                        "Failed to load proxy logs",
                    { variant: "warning" }
                );
                return null;
            } finally {
                handleLoader(false);
            }
        };

        const copyUrl = async (url: string) => {
            try {
                await navigator.clipboard.writeText(url);
                enqueueSnackbar("Proxy URL copied", { variant: "success" });
            } catch {
                enqueueSnackbar("Unable to copy URL", { variant: "error" });
            }
        };

        const handleChangePage = async (
            _event: unknown,
            newPage: number
        ) => {
            if (newPage > page) {
                const skip = skipKeyQueue[page];
                const data = await fetchPage(
                    rowsPerPage,
                    skip || "",
                    skipKeyQueue,
                    filterOptions
                );
                if (data?.list) {
                    setRows(data.list);
                    setPage(newPage);
                }
            } else if (newPage === 0) {
                await getProxyLogsList();
            } else {
                setPage(newPage);
            }
        };

        const handleChangeRowsPerPage = async (
            event: React.ChangeEvent<HTMLInputElement>
        ) => {
            const next = parseInt(event.target.value, 10);
            setRowsPerPage(next);
            setPage(0);
            const data = await fetchPage(next, "", [], filterOptions);
            if (data) {
                setTotalCount(data.totalCount || 0);
                setRows([...(data.list || [])]);
            }
        };

        return (
            <Paper>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <h3>File name</h3>
                                </TableCell>
                                <TableCell>
                                    <h3>HD</h3>
                                </TableCell>
                                <TableCell>
                                    <h3>Status</h3>
                                </TableCell>
                                <TableCell>
                                    <h3>Proxy URL</h3>
                                </TableCell>
                                <TableCell align="right">
                                    <h3>Started At</h3>
                                </TableCell>
                                <TableCell align="right">
                                    <h3>Expires At</h3>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell />
                                <TableCell />
                                <TableCell>
                                    <FormControl component="fieldset">
                                        <FormGroup>
                                            {INGESTION_STATUS.map(
                                                (status: any) => (
                                                    <FormControlLabel
                                                        key={status.key}
                                                        style={{
                                                            height: "30px",
                                                        }}
                                                        control={
                                                            <Checkbox
                                                                checked={filterOptions.status.includes(
                                                                    status.key
                                                                )}
                                                                onChange={(
                                                                    e: any
                                                                ) =>
                                                                    onFilterOptionsChange(
                                                                        {
                                                                            status: e
                                                                                .target
                                                                                .value,
                                                                        }
                                                                    )
                                                                }
                                                                name={
                                                                    status.label
                                                                }
                                                                value={
                                                                    status.key
                                                                }
                                                            />
                                                        }
                                                        label={status.label}
                                                    />
                                                )
                                            )}
                                        </FormGroup>
                                    </FormControl>
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <TableCell align="right">
                                    <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                    >
                                        <DatePicker
                                            label="Completed before"
                                            value={
                                                filterOptions.completedBefore
                                                    ? dayjs.utc(
                                                          filterOptions.completedBefore
                                                      )
                                                    : null
                                            }
                                            onChange={(value) =>
                                                onFilterOptionsChange({
                                                    completedBefore: value
                                                        ? value
                                                              .utc()
                                                              .endOf("day")
                                                              .valueOf()
                                                        : null,
                                                })
                                            }
                                            slotProps={{
                                                textField: {
                                                    size: "small",
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>
                                        {row.studioName
                                            ? `${row.studioName}/`
                                            : ""}
                                        {row.ingestionName}
                                    </TableCell>
                                    <TableCell>
                                        {row.hd ? "Yes" : "No"}
                                    </TableCell>
                                    <TableCell>
                                        {row.ingestionStatus}
                                        {row.errorMessage
                                            ? ` — ${row.errorMessage}`
                                            : ""}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 360 }}>
                                        {row.ingestionStatus === "COMPLETED" &&
                                        row.proxyUrl ? (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: 0.5,
                                                    minWidth: 0,
                                                }}
                                            >
                                                <Tooltip title={row.proxyUrl}>
                                                    <Typography
                                                        component="a"
                                                        href={row.proxyUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        variant="body2"
                                                        noWrap
                                                        sx={{
                                                            flex: 1,
                                                            minWidth: 0,
                                                            color: "primary.main",
                                                            textDecoration: "none",
                                                            "&:hover": {
                                                                textDecoration: "underline",
                                                            },
                                                        }}
                                                    >
                                                        {row.proxyUrl}
                                                    </Typography>
                                                </Tooltip>
                                                <Button
                                                    size="small"
                                                    startIcon={
                                                        <ContentCopyIcon fontSize="small" />
                                                    }
                                                    onClick={() =>
                                                        copyUrl(row.proxyUrl)
                                                    }
                                                >
                                                    Copy
                                                </Button>
                                            </Box>
                                        ) : (
                                            "—"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.startedAt
                                            ? MILLIS_TO_LOCALE(row.startedAt)
                                            : "—"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {row.expiresAt
                                            ? MILLIS_TO_LOCALE(row.expiresAt)
                                            : "—"}
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!rows.length && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        No proxy logs yet
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        );
    }
);

export default ProxyLogsTable;
